import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Result,
  Select,
  Space,
  Typography,
  message
} from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useWorkOrderTemplate } from '../../hooks/useWorkOrderTemplates';
import type { ChecklistItem } from '../../../../shared/types/work-order-template.types';
import { withDevModalFilePath, NumericInput, DateDisplay } from '../common';
import { getMutationPendingState } from '../../utils/trpcMutation';

const { Modal } = withDevModalFilePath('client/src/components/WorkSchedule/ExecuteModal.tsx');
import type { WorkScheduleResult } from '../../../../shared/types/work-schedule.types';
import { trpc } from '../../lib/api';
import type { WorkScheduleDetail } from './DetailModal';

const { Text, Paragraph } = Typography;

const RESULT_VALUES: WorkScheduleResult[] = ['good', 'attention_needed', 'repair_required'];

const RESULT_OPTIONS = RESULT_VALUES.map((value) => ({
  label:
    value === 'good'
      ? '良好'
      : value === 'attention_needed'
        ? '要注意'
        : '修理必要',
  value
}));

type ChecklistValue = string | number | boolean | Dayjs | null;

type ChecklistState = {
  completed: boolean;
  value?: ChecklistValue;
  notes?: string;
};

type ExistingChecklistResultLike = {
  itemId?: unknown;
  checklistItemId?: unknown;
  completed?: unknown;
  value?: unknown;
  note?: unknown;
  notes?: unknown;
  completedAt?: unknown;
};

interface WorkScheduleExecuteModalProps {
  open: boolean;
  schedule: WorkScheduleDetail | null;
  onClose: () => void;
  onCompleted?: () => void;
}

export function WorkScheduleExecuteModal({
  open,
  schedule,
  onClose,
  onCompleted
}: WorkScheduleExecuteModalProps): JSX.Element {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const templateId = open ? schedule?.workOrderTemplateId ?? null : null;
  const { template } = useWorkOrderTemplate(templateId, {
    includeEquipments: false
  });

  const checklistItems = useMemo<ChecklistItem[]>(() => {
    return template?.checklistItems ?? [];
  }, [template]);

  const initialChecklistState = useMemo<Record<string, ChecklistState>>(() => {
    const rawChecklistResults = schedule?.checklistResults as unknown;
    const rawItems: unknown[] = Array.isArray(rawChecklistResults)
      ? rawChecklistResults
      : rawChecklistResults &&
          typeof rawChecklistResults === 'object' &&
          'items' in rawChecklistResults &&
          Array.isArray((rawChecklistResults as { items?: unknown }).items)
        ? ((rawChecklistResults as { items: unknown[] }).items ?? [])
        : [];

    const existingMap = new Map<string, { completed: boolean; value?: unknown; notes?: string }>();

    rawItems.forEach((rawItem) => {
      const item = rawItem as ExistingChecklistResultLike;
      const itemId =
        typeof item.itemId === 'string'
          ? item.itemId
          : typeof item.checklistItemId === 'string'
            ? item.checklistItemId
            : null;

      if (!itemId) {
        return;
      }

      const notes =
        typeof item.notes === 'string'
          ? item.notes
          : typeof item.note === 'string'
            ? item.note
            : undefined;

      const completed =
        typeof item.completed === 'boolean'
          ? item.completed
          : typeof item.value === 'boolean'
            ? item.value
            : Boolean(item.completedAt);

      existingMap.set(itemId, {
        completed,
        value: item.value,
        notes
      });
    });

    const defaults: Record<string, ChecklistState> = {};

    checklistItems.forEach((item) => {
      const existing = existingMap.get(item.id);
      let value: ChecklistValue | undefined;
      if (existing?.value !== undefined && existing.value !== null) {
        if (item.type === 'date') {
          value = dayjs(existing.value as string);
        } else {
          value = existing.value as ChecklistValue;
        }
      }
      defaults[item.id] = {
        completed: existing?.completed ?? (item.type === 'check' ? false : Boolean(value)),
        value,
        notes: existing?.notes ?? ''
      };
    });

    if (checklistItems.length === 0 && existingMap.size > 0) {
      // Preserve existing results even if template is missing
      existingMap.forEach((result, itemId) => {
        defaults[itemId] = {
          completed: result.completed,
          value:
            typeof result.value === 'string' || typeof result.value === 'number' || typeof result.value === 'boolean'
              ? (result.value as ChecklistValue)
              : null,
          notes: result.notes ?? ''
        };
      });
    }

    return defaults;
  }, [schedule, checklistItems]);

  const [checklistState, setChecklistState] = useState<Record<string, ChecklistState>>({});
  const [hasStartedLocally, setHasStartedLocally] = useState(false);

  useEffect(() => {
    if (!open) {
      setChecklistState(initialChecklistState);
      setHasStartedLocally(false);
      return;
    }

    setChecklistState(initialChecklistState);
    setHasStartedLocally(Boolean(schedule?.actualStart));

    if (!schedule) {
      return;
    }

    form.setFieldsValue({
      workPerformed: schedule.workPerformed ?? '',
      findings: schedule.findings ?? '',
      recommendations: schedule.recommendations ?? '',
      result: schedule.result ?? undefined
    });
  }, [open, schedule, form, initialChecklistState]);

  const startMutation = trpc.schedule.startWork.useMutation();

  const completeMutation = trpc.schedule.completeWork.useMutation({
    onSuccess: () => {
      messageApi.success('作業を完了しました');
      onCompleted?.();
      onClose();
    },
    onError: (error) => {
      messageApi.error(error.message || '作業の完了に失敗しました');
    }
  });

  const updateChecklistState = (itemId: string, patch: Partial<ChecklistState>) => {
    setChecklistState((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        ...patch
      }
    }));
  };

  const runStartMutation = async (actualStart: Date, options?: { silent?: boolean }) => {
    if (!schedule) return;

    const silent = options?.silent ?? false;
    try {
      await startMutation.mutateAsync({
        id: schedule.id,
        actualStart
      });
      if (!silent) {
        messageApi.success('作業を開始しました');
      }
      setHasStartedLocally(true);
    } catch (error) {
      const fallback = '作業の開始に失敗しました';
      if (!silent) {
        messageApi.error(error instanceof Error && error.message ? error.message : fallback);
      }
      throw error;
    }
  };

  const handleStart = async () => {
    if (!schedule) return;
    await runStartMutation(new Date());
  };

  const validateChecklist = (): boolean => {
    for (const item of checklistItems) {
      const state = checklistState[item.id] ?? { completed: false };
      if (item.required) {
        if (item.type === 'check') {
          if (!state.completed) {
            messageApi.error(`必須項目「${item.title}」が未完了です。`);
            return false;
          }
        } else {
          if (
            state.value === undefined ||
            state.value === null ||
            (typeof state.value === 'string' && state.value.trim() === '')
          ) {
            messageApi.error(`必須項目「${item.title}」に入力が必要です。`);
            return false;
          }
        }
      }
    }
    return true;
  };

  const handleComplete = async () => {
    if (!schedule) return;

    try {
      const values = await form.validateFields();
      if (!validateChecklist()) {
        return;
      }

      const canStart =
        !schedule.actualStart &&
        !hasStartedLocally &&
        (schedule.status === 'scheduled' || schedule.status === 'postponed');

      if (canStart) {
        const now = dayjs();
        const scheduled = schedule.scheduledDate ? dayjs(schedule.scheduledDate) : null;
        const autoStartAt =
          scheduled && scheduled.isBefore(now)
            ? scheduled.toDate()
            : now.subtract(1, 'minute').toDate();

        try {
          await runStartMutation(autoStartAt, { silent: true });
        } catch {
          return;
        }
      }

      const checklistResults = checklistItems.map((item) => {
        const state = checklistState[item.id] ?? { completed: false };
        let value: string | number | boolean | null;

        if (item.type === 'check') {
          value = state.completed;
        } else if (state.value !== undefined && state.value !== null) {
          if (item.type === 'date' && dayjs.isDayjs(state.value)) {
            value = (state.value as Dayjs).toISOString();
          } else if (item.type === 'number') {
            value = Number(state.value);
          } else {
            value = state.value as string | number | boolean;
          }
        } else {
          value = null;
        }

        const hasValue =
          value !== null &&
          value !== undefined &&
          !(typeof value === 'string' && value.trim() === '');
        const note = state.notes?.trim() ? state.notes.trim() : undefined;

        return {
          checklistItemId: item.id,
          itemId: item.id,
          completed: item.type === 'check' ? state.completed : hasValue || state.completed,
          value,
          note,
          notes: note
        };
      });

      await completeMutation.mutateAsync({
        id: schedule.id,
        actualEnd: new Date(),
        result: values.result as WorkScheduleResult | undefined,
        workPerformed: values.workPerformed ?? undefined,
        findings: values.findings ?? undefined,
        recommendations: values.recommendations ?? undefined,
        checklistResults
      });
    } catch (error) {
      if (error instanceof Error) {
        messageApi.error(error.message);
      }
    }
  };

  const renderInput = (item: ChecklistItem, state: ChecklistState) => {
    switch (item.type) {
      case 'check':
        return (
          <Checkbox
            checked={state.completed}
            onChange={(event) =>
              updateChecklistState(item.id, { completed: event.target.checked })
            }
          >
            {item.description ?? '完了'}
          </Checkbox>
        );
      case 'text':
        return (
          <Input.TextArea
            rows={3}
            value={(state.value as string) ?? ''}
            onChange={(event) =>
              updateChecklistState(item.id, { value: event.target.value })
            }
            placeholder={item.description ?? '内容を入力'}
          />
        );
      case 'number':
        return (
          <NumericInput
            style={{ width: '100%' }}
            value={state.value as number | undefined}
            onChange={(value) => updateChecklistState(item.id, { value: value ?? null })}
          />
        );
      case 'date':
        return (
          <DatePicker
            style={{ width: '100%' }}
            value={dayjs.isDayjs(state.value) ? (state.value as Dayjs) : undefined}
            onChange={(value) => updateChecklistState(item.id, { value: value ?? null })}
          />
        );
      case 'select':
        return (
          <Select
            style={{ width: '100%' }}
            value={(state.value as string) ?? undefined}
            onChange={(value) => updateChecklistState(item.id, { value })}
            options={(item.options ?? []).map((option) => ({ label: option, value: option }))}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      title="作業報告"
      open={open}
      onCancel={onClose}
      width={720}
      destroyOnHidden
      footer={null}
    >
      {contextHolder}
      {!schedule ? (
        <Result status="info" title="スケジュール情報が見つかりません" />
      ) : (
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
          <Card size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text type="secondary">対象スケジュール</Text>
              <Paragraph style={{ marginBottom: 0 }}>{schedule.title}</Paragraph>
              <Text type="secondary">
                予定日時: <DateDisplay value={schedule.scheduledDate} showTime type="secondary" />
              </Text>
            </Space>
          </Card>

          <Form layout="vertical" form={form}>
            <Form.Item label="作業内容" name="workPerformed">
              <Input.TextArea rows={4} placeholder="作業の内容を入力" />
            </Form.Item>
            <Form.Item label="発見事項" name="findings">
              <Input.TextArea rows={3} placeholder="発見事項を入力" />
            </Form.Item>
            <Form.Item label="推奨事項" name="recommendations">
              <Input.TextArea rows={3} placeholder="推奨事項を入力" />
            </Form.Item>
            <Form.Item label="結果" name="result">
              <Select
                allowClear
                options={RESULT_OPTIONS}
                placeholder="結果を選択"
              />
            </Form.Item>
          </Form>

          {checklistItems.length > 0 && (
            <Space direction="vertical" style={{ width: '100%' }} size={16}>
              <Text strong>チェックリスト</Text>
              {checklistItems.map((item) => {
                const state = checklistState[item.id] ?? { completed: false };
                return (
                  <Card key={item.id} size="small" title={item.title}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {item.description && (
                        <Text type="secondary">{item.description}</Text>
                      )}
                      {renderInput(item, state)}
                      <Input.TextArea
                        rows={2}
                        value={state.notes ?? ''}
                        onChange={(event) =>
                          updateChecklistState(item.id, { notes: event.target.value })
                        }
                        placeholder="備考"
                      />
                    </Space>
                  </Card>
                );
              })}
            </Space>
          )}

            <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            {(schedule.status === 'scheduled' || schedule.status === 'postponed') && (
              <Button onClick={handleStart} loading={getMutationPendingState(startMutation)}>
                作業開始
              </Button>
            )}
            <Button type="primary" onClick={handleComplete} loading={getMutationPendingState(completeMutation)}>
              完了
            </Button>
          </Space>
        </Space>
      )}
    </Modal>
  );
}
